<script lang="ts" setup>
import { Card, CardContent, CardHeader, CardTitle } from '~ds/components/ui/card';
import { Icon } from '~ds/components/ui/icon';
import { Separator } from '~ds/components/ui/separator';
import { Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';

export interface SpotlightLink {
  title: string;
  url: string;
  description?: string | null;
}

// What a placement hands over once it has decided a spotlight should be shown. The header is
// authored as image and/or heading — either alone is a valid configuration, and an image always
// carries its own alt text so an image-only header stays legible to assistive tech.
export interface SpotlightContent {
  heading: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
  links: SpotlightLink[];
}

withDefaults(defineProps<SpotlightContent>(), {
  imageUrl: null,
  imageAlt: null,
});

const emit = defineEmits<{
  linkClick: [link: SpotlightLink];
}>();
</script>

<template>
  <!-- A spotlight is its links; without any there is nothing to point at, so render nothing. -->
  <!-- The flat card is borderless by default, which leaves it invisible on the white surfaces
       it ships on (the overview, the badge page) — so it carries a hairline, the one treatment
       that reads on a white AND an off-white parent. -->
  <Card
    v-if="links.length"
    class="border-border border"
  >
    <CardHeader v-if="imageUrl || heading">
      <!-- A short, left-aligned slot at the image's own aspect ratio — sized for a partner
           logo or banner strip, not a hero: a full-width cover crop would dominate the card. -->
      <NuxtPicture
        v-if="imageUrl"
        :src="imageUrl"
        format="avif,webp"
        sizes="320px"
        :img-attrs="{ alt: imageAlt ?? '', class: 'h-16 w-auto max-w-full rounded object-contain' }"
      />
      <!-- The leading is restated AFTER the size — tailwind-merge prunes a leading that precedes
           a font-size, so CardTitle's own `leading-none` would silently drop. -->
      <CardTitle
        v-if="heading"
        class="text-xl leading-tight"
      >
        {{ heading }}
      </CardTitle>
    </CardHeader>

    <CardContent>
      <ul>
        <li
          v-for="(link, index) in links"
          :key="link.url"
        >
          <Separator v-if="index > 0" />
          <NuxtLink
            :to="link.url"
            external
            target="_blank"
            class="group flex items-center justify-between gap-4 py-3"
            @click.stop="emit('linkClick', link)"
          >
            <span class="flex min-w-0 flex-col gap-1">
              <Text
                as="span"
                size="md"
                weight="semibold"
                class="underline-offset-4 group-hover:underline"
              >
                {{ link.title }}
              </Text>
              <Text
                v-if="link.description"
                as="span"
                size="sm"
                class="text-muted-foreground"
              >
                {{ link.description }}
              </Text>
            </span>
            <Icon
              :name="ICONS.arrowUpRightSolid"
              size="sm"
              class="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </NuxtLink>
        </li>
      </ul>
    </CardContent>
  </Card>
</template>
